import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Crown, EyeOff, Eye, Lock, Share2, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { openWhatsappVotes } from "../lib/padel";
import { WinnersShowcase, hasWinners } from "./AwardsCeremony";
import { CampaignChat } from "./CampaignChat";
import { Avatar } from "./Avatar";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const ICONS = { destra: "👉", sinistra: "👈", sorpresa: "⚖️", leale: "🤝", disonesto: "🦊", professore: "🎓" };
const MEDALS = ["bg-[#ccff00] text-[#090d16]", "bg-slate-300 text-[#090d16]", "bg-[#cd7f32] text-[#090d16]"];

const Podium = ({ ranking, catId, isOpen }) => {
  if (!ranking.length) {
    return <p className="text-sm text-slate-500 py-4" data-testid={`vote-empty-${catId}`}>{isOpen ? "Nessun voto ancora. Sii il primo!" : "Nessun voto"}</p>;
  }
  return (
    <ol className="space-y-2" data-testid={`vote-ranking-${catId}`}>
      {ranking.slice(0, 5).map((r, i) => (
        <li key={r.key} className="flex items-start gap-3 bg-[#0b1420] rounded-xl px-3 py-2.5" data-testid={`vote-row-${catId}-${r.key}`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold shrink-0 ${MEDALS[i] || "bg-[#1e2c4a] text-slate-300"}`}>
            {i === 0 ? <Crown className="w-3.5 h-3.5" /> : i + 1}
          </span>
          <Avatar name={r.name} photoPath={r.photo_path} size={32} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-slate-100 truncate">{r.name}</p>
              <span className="text-sm font-bold text-[#ccff00] shrink-0" data-testid={`vote-count-${catId}-${r.key}`}>
                {r.votes} {r.votes === 1 ? "voto" : "voti"}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {r.voters.length > 0 && <>Votato da {r.voters.join(", ")}</>}
              {r.secret_votes > 0 && (
                <span className="inline-flex items-center gap-1 ml-1">
                  <Lock className="w-3 h-3" /> {r.secret_votes} segret{r.secret_votes === 1 ? "o" : "i"}
                </span>
              )}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
};

const VoteForm = ({ cat, candidates, isOpen, onSaved }) => {
  const [candidate, setCandidate] = useState(cat.my_vote?.candidate || "");
  const [secret, setSecret] = useState(cat.my_vote?.secret || false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setCandidate(cat.my_vote?.candidate || "");
    setSecret(cat.my_vote?.secret || false);
  }, [cat.my_vote]);

  if (!isOpen) {
    return cat.my_vote ? <p className="text-xs text-slate-400">Hai votato {cat.my_vote.candidate}{cat.my_vote.secret ? " (segreto)" : ""}.</p> : null;
  }
  const run = async (fn, msg) => {
    setSaving(true);
    try { await fn(); toast.success(msg); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setSaving(false); }
  };
  const save = () => run(() => api.castVote(cat.id, candidate, secret), "Voto registrato!");
  const remove = () => run(() => api.removeVote(cat.id), "Voto ritirato");
  const changed = candidate && (candidate !== cat.my_vote?.candidate || secret !== cat.my_vote?.secret);

  return (
    <div className="space-y-3 border-t border-[#1e2c4a] pt-4">
      <Select value={candidate} onValueChange={setCandidate}>
        <SelectTrigger data-testid={`vote-select-${cat.id}`} className="bg-[#0b1420] border-[#1e2c4a] text-slate-100 h-11 rounded-xl">
          <SelectValue placeholder="Scegli il tuo candidato" />
        </SelectTrigger>
        <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
          {candidates.map((c) => (
            <SelectItem key={c.name} value={c.name} data-testid={`vote-option-${cat.id}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label className="flex items-center justify-between gap-3 text-sm text-slate-300 cursor-pointer">
        <span className="flex items-center gap-2">
          {secret ? <EyeOff className="w-4 h-4 text-[#ff9f1c]" /> : <Eye className="w-4 h-4 text-[#25D366]" />}
          {secret ? "Voto segreto: nessuno vedrà chi hai votato" : "Voto palese: il tuo nome sarà visibile"}
        </span>
        <Switch checked={secret} onCheckedChange={setSecret} data-testid={`vote-secret-switch-${cat.id}`} />
      </label>
      <div className="flex gap-2">
        <Button onClick={save} disabled={!changed || saving} data-testid={`vote-submit-${cat.id}`}
          className="flex-1 bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-full h-10">
          {cat.my_vote ? "Aggiorna voto" : "Vota"}
        </Button>
        {cat.my_vote && (
          <Button onClick={remove} disabled={saving} variant="outline" data-testid={`vote-remove-${cat.id}`}
            className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full h-10">
            Ritira
          </Button>
        )}
      </div>
    </div>
  );
};

export const VotesBoard = ({ playerName }) => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.getVotes(year).then(setData).catch(() => toast.error("Impossibile caricare le votazioni"));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="votes-board">
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="votes-year-switch">
        {[thisYear, thisYear - 1].map((y) => (
          <button key={y} onClick={() => setYear(y)} data-testid={`votes-year-${y}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${year === y ? "bg-[#ccff00] text-[#090d16]" : "bg-[#121b2d] border border-[#1e2c4a] text-slate-300 hover:text-white"}`}>
            {y}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-1" data-testid="votes-status">
          {data?.is_open ? `Aperta fino al 31/12/${year}` : "Votazione chiusa"}
        </span>
        {hasWinners(data) && (
          <Button onClick={() => openWhatsappVotes(data)} data-testid="votes-share-btn"
            className="ml-auto bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full h-9 px-3 text-xs">
            <Share2 className="w-3.5 h-3.5 mr-1" /> Condividi podio
          </Button>
        )}
      </div>
      {!data ? (
        <p className="text-slate-500 text-sm">Caricamento…</p>
      ) : (
        <>
        {(!data.is_open && hasWinners(data)) && (
          <div className="mb-5" data-testid="votes-past-awards">
            <h3 className="font-heading font-bold text-lg text-slate-100 mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#ccff00]" /> Vincitori {data.year}
            </h3>
            <WinnersShowcase data={data} />
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-3">
          {data.categories.map((cat) => (
            <section key={cat.id} className="rounded-2xl bg-[#121b2d] border border-[#1e2c4a] p-4 space-y-4" data-testid={`vote-category-${cat.id}`}>
              <header>
                <p className="text-2xl">{ICONS[cat.id]}</p>
                <h3 className="font-heading font-bold text-lg text-slate-100 leading-tight">{cat.label}</h3>
                <p className="text-xs text-slate-500">{cat.total_votes} {cat.total_votes === 1 ? "voto totale" : "voti totali"}</p>
              </header>
              <Podium ranking={cat.ranking} catId={cat.id} isOpen={data.is_open} />
              <VoteForm cat={cat} candidates={data.candidates} isOpen={data.is_open} onSaved={load} />
            </section>
          ))}
        </div>
        </>
      )}
      <CampaignChat playerName={playerName} names={(data?.candidates || []).map((c) => c.name)} />
    </div>
  );
};
